import { assertNever } from '@sparkdotfi/common-universal'
import { InfoIcon } from 'lucide-react'
import { HTMLAttributes } from 'react'

import { TransactionOverviewApyChange } from './rows/TransactionOverviewApyChange'
import { TransactionOverviewApyWithRewards } from './rows/TransactionOverviewApyWithRewards'
import { TransactionOverviewAvailableAssets } from './rows/TransactionOverviewAvailableAssets'
import { TransactionOverviewHealthFactorChange } from './rows/TransactionOverviewHealthFactorChange'
import { TransactionOverviewMaxLtvChange } from './rows/TransactionOverviewMaxLtvChange'
import { TransactionOverviewSavingsApy } from './rows/TransactionOverviewSavingsApy'
import { TransactionOverviewSavingsBalanceChange } from './rows/TransactionOverviewSavingsBalanceChange'
import { TransactionOverviewSavingsOutcome } from './rows/TransactionOverviewSavingsOutcome'
import { TransactionOverviewSlippage } from './rows/TransactionOverviewSlippage'
import { TransactionOverviewStakedInFarm } from './rows/TransactionOverviewStakedInFarm'
import { TransactionOverviewTokenAmount } from './rows/TransactionOverviewTokenAmount'
import { TransactionOverviewTokenAmountChange } from './rows/TransactionOverviewTokenAmountChange'
import { TransactionOverviewTokenAmountOutcome } from './rows/TransactionOverviewTokenAmountOutcome'
import { tokenLogos } from '@/config/chain/configs/mainnet/assets'
import { ProvidedBy } from '@/features/savings/dialogs/common/types'
import { assets } from '@/ui/assets'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { links } from '@/ui/constants/links'
import { cn } from '@/ui/utils/style'

export interface TransactionOverviewProps {
  children: React.ReactNode
  providedBy?: ProvidedBy
}

function TransactionOverview({ children, providedBy }: TransactionOverviewProps) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h5 className="typography-body-3 text-secondary">Transaction overview</h5>
        {providedBy && <ProviderBadge provider={providedBy} />}
      </div>
      <div
        className="grid grid-cols-[auto_1fr] gap-x-6 rounded-sm bg-secondary"
        style={{ gridAutoRows: 'minmax(0, 1fr)' }}
      >
        {children}
      </div>
    </section>
  )
}

function TransactionOverviewLabel({ children }: { children: React.ReactNode }) {
  return <div className="typography-label-3 flex items-center gap-1 text-secondary">{children}:</div>
}

function TransactionOverviewRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'col-span-full grid grid-cols-subgrid items-center',
        'border-primary border-b px-5 py-4 last:border-none sm:min-h-[68px]',
        '[&:not(:has(>*:nth-child(2)))]:hidden', // there is no second child, e.g. either or both label and content are missing
      )}
    >
      {children}
    </div>
  )
}

function ProviderBadge({ provider }: { provider: ProvidedBy }) {
  switch (provider) {
    case 'sky':
      return (
        <div className="flex items-center gap-1.5">
          <img src={tokenLogos.sky} className="icon-size-xs" />
          <div className="typography-label-4 text-brand-primary">Powered by Sky</div>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="icon-size-xs icon-brand-primary" />
            </TooltipTrigger>
            <TooltipContent>The transaction uses infrastructure provided by the Sky Ecosystem.</TooltipContent>
          </Tooltip>
        </div>
      )
    case 'spark':
      return (
        <div className="flex items-center gap-1.5">
          <img src={assets.sparkLiquidityLayerLogo} className="icon-size-xs" />
          <div className="typography-label-4 text-brand-primary">Powered by Spark</div>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="icon-size-xs icon-brand-primary" />
            </TooltipTrigger>
            <TooltipContent>
              The transaction uses infrastructure provided by the Spark Liquidity Layer.{' '}
              {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
                <ExternalLink to={links.docs.sparkLiquidityLayer}>Learn more</ExternalLink>
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      )
    default:
      assertNever(provider)
  }
}

function TransactionOverviewGenericRow({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div {...rest} className={cn('typography-label-2 text-primary', className)} />
}

TransactionOverview.Label = TransactionOverviewLabel
TransactionOverview.Row = TransactionOverviewRow

TransactionOverview.TokenAmount = TransactionOverviewTokenAmount
TransactionOverview.TokenAmountOutcome = TransactionOverviewTokenAmountOutcome
TransactionOverview.SavingsOutcome = TransactionOverviewSavingsOutcome
TransactionOverview.SavingsBalanceChange = TransactionOverviewSavingsBalanceChange
TransactionOverview.ApyWithRewards = TransactionOverviewApyWithRewards
TransactionOverview.SavingsApy = TransactionOverviewSavingsApy
TransactionOverview.ApyChange = TransactionOverviewApyChange
TransactionOverview.TokenAmountChange = TransactionOverviewTokenAmountChange
TransactionOverview.HealthFactorChange = TransactionOverviewHealthFactorChange
TransactionOverview.AvailableAssets = TransactionOverviewAvailableAssets
TransactionOverview.MaxLtvChange = TransactionOverviewMaxLtvChange
TransactionOverview.StakedInFarm = TransactionOverviewStakedInFarm
TransactionOverview.Slippage = TransactionOverviewSlippage
TransactionOverview.Generic = TransactionOverviewGenericRow

export { TransactionOverview }
